import { useEffect, useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import axios from '../../lib/axios'
import { ArrowLeft, Send, Eye, RefreshCw, AlertCircle } from 'lucide-react'
import '../Pages.css'

// Extract unique {{variable}} names from text
function extractVariables(text) {
  if (!text) return []
  const matches = text.match(/\{\{\s*(\w+)\s*\}\}/g) || []
  return [...new Set(matches.map((m) => m.replace(/\{\{\s*|\s*\}\}/g, '')))]
}

// Replace {{variable}} placeholders with provided values
function replaceVariables(text, values) {
  if (!text) return text
  return text.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, name) => {
    return values[name] !== undefined && values[name] !== '' ? values[name] : match
  })
}

export default function NewPlacementTest() {
  const navigate = useNavigate()
  const [campaigns, setCampaigns] = useState([])
  const [emailAccounts, setEmailAccounts] = useState([])
  const [selectedCampaignId, setSelectedCampaignId] = useState('')
  const [steps, setSteps] = useState([])
  const [selectedStepVariant, setSelectedStepVariant] = useState('')
  const [selectedEmailAccountId, setSelectedEmailAccountId] = useState('')
  const [preview, setPreview] = useState(null)
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [stepsLoading, setStepsLoading] = useState(false)
  const [variableValues, setVariableValues] = useState({})

  // Fetch campaigns and email accounts on mount
  useEffect(() => {
    setLoading(true)
    Promise.all([
      axios.get('email-campaign/get-all'),
      axios.get('email-campaign/get-email-accounts')
    ])
      .then(([campaignsRes, accountsRes]) => {
        setCampaigns(Array.isArray(campaignsRes.data) ? campaignsRes.data : [])
        const accounts = accountsRes.data
        setEmailAccounts(Array.isArray(accounts) ? accounts : accounts.data || accounts.accounts || [])
      })
      .catch((err) => {
        console.error('Error loading form data:', err)
      })
      .finally(() => setLoading(false))
  }, [])

  // Fetch sequence steps when campaign is selected
  useEffect(() => {
    if (!selectedCampaignId) {
      setSteps([])
      setSelectedStepVariant('')
      setPreview(null)
      return
    }
    setStepsLoading(true)
    setSelectedStepVariant('')
    setPreview(null)
    axios
      .get(`email-campaign/get-sequence/${selectedCampaignId}`)
      .then((res) => {
        const data = res.data
        const rawSteps = data?.steps || []
        // Build flat list of step+variant options
        const options = []
        rawSteps.forEach((step, stepIdx) => {
          const variants = step.variants || []
          variants.forEach((variant) => {
            options.push({
              key: `${stepIdx}-${variant.id}`,
              label: `Step ${stepIdx + 1} - Variant ${variant.id}`,
              subject: variant.subject || '',
              body: variant.body || ''
            })
          })
        })
        setSteps(options)
      })
      .catch((err) => {
        console.error('Error loading sequence:', err)
        setSteps([])
      })
      .finally(() => setStepsLoading(false))
  }, [selectedCampaignId])

  // Update preview when step/variant is selected
  useEffect(() => {
    if (!selectedStepVariant) {
      setPreview(null)
      setVariableValues({})
      return
    }
    const selected = steps.find((s) => s.key === selectedStepVariant)
    if (selected) {
      setPreview({ subject: selected.subject, body: selected.body })
      // Reset variable values when step changes
      setVariableValues({})
    }
  }, [selectedStepVariant, steps])

  // Detect all variables from subject + body
  const detectedVariables = useMemo(() => {
    if (!preview) return []
    const subjectVars = extractVariables(preview.subject)
    const bodyVars = extractVariables(preview.body)
    return [...new Set([...subjectVars, ...bodyVars])]
  }, [preview])

  // Check if all variables are filled
  const allVariablesFilled = useMemo(() => {
    if (detectedVariables.length === 0) return true
    return detectedVariables.every((v) => variableValues[v] && variableValues[v].trim() !== '')
  }, [detectedVariables, variableValues])

  // Get the final subject/body with variables replaced
  const finalSubject = useMemo(() => {
    if (!preview) return ''
    return replaceVariables(preview.subject, variableValues)
  }, [preview, variableValues])

  const finalBody = useMemo(() => {
    if (!preview) return ''
    return replaceVariables(preview.body, variableValues)
  }, [preview, variableValues])

  const handleVariableChange = (name, value) => {
    setVariableValues((prev) => ({ ...prev, [name]: value }))
  }

  // Format variable name for display (e.g. "user_name" -> "User Name")
  const formatVariableName = (name) => {
    return name
      .replace(/_/g, ' ')
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/\b\w/g, (c) => c.toUpperCase())
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!selectedCampaignId || !selectedStepVariant || !selectedEmailAccountId) {
      alert('Please fill in all fields')
      return
    }
    if (!preview) {
      alert('Please select a step/variant to preview')
      return
    }
    if (!allVariablesFilled) {
      alert('Please fill in all variable values before running the test')
      return
    }
    setSubmitting(true)
    try {
      const res = await axios.post('api/placement/tests', {
        email_account_id: selectedEmailAccountId,
        subject: finalSubject,
        body: finalBody
      })
      const testId = res.data?.id || res.data?.test_id
      if (testId) {
        navigate(`/inbox-placement/${testId}`)
      } else {
        navigate('/inbox-placement')
      }
    } catch (err) {
      console.error('Error creating placement test:', err)
      alert(err.response?.data?.detail || 'Failed to create placement test. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <div className="page animate-fade-in">
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <div className="loader"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="page animate-fade-in">
      <div className="page-header">
        <div className="header-info">
          <h2>New Placement Test</h2>
          <p>Select a campaign and step to test inbox placement</p>
        </div>
        <button onClick={() => navigate('/inbox-placement')} className="btn btn-ghost">
          <ArrowLeft size={18} />
          <span>Back to Tests</span>
        </button>
      </div>

      <div className="card" style={{ maxWidth: '700px', margin: '0 auto' }}>
        <div className="card-header">
          <h3>Test Configuration</h3>
        </div>
        <div style={{ padding: '1.5rem' }}>
          <form onSubmit={handleSubmit}>
            {/* Campaign Dropdown */}
            <div className="form-group" style={{ marginBottom: '1.25rem' }}>
              <label>Campaign *</label>
              <select
                value={selectedCampaignId}
                onChange={(e) => setSelectedCampaignId(e.target.value)}
                disabled={submitting}
              >
                <option value="">-- Select Campaign --</option>
                {campaigns.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
              <span style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', marginTop: '0.25rem', display: 'block' }}>
                Choose the campaign whose email content you want to test
              </span>
            </div>

            {/* Step/Variant Dropdown */}
            <div className="form-group" style={{ marginBottom: '1.25rem' }}>
              <label>Step &amp; Variant *</label>
              <select
                value={selectedStepVariant}
                onChange={(e) => setSelectedStepVariant(e.target.value)}
                disabled={!selectedCampaignId || stepsLoading || submitting}
              >
                <option value="">
                  {stepsLoading ? 'Loading steps...' : !selectedCampaignId ? 'Select a campaign first' : '-- Select Step & Variant --'}
                </option>
                {steps.map((s) => (
                  <option key={s.key} value={s.key}>{s.label}</option>
                ))}
              </select>
              {selectedCampaignId && !stepsLoading && steps.length === 0 && (
                <span style={{ fontSize: '0.8rem', color: '#ef4444', marginTop: '0.25rem', display: 'block' }}>
                  No sequence steps found for this campaign. Please configure the campaign sequence first.
                </span>
              )}
            </div>

            {/* Email Account Dropdown */}
            <div className="form-group" style={{ marginBottom: '1.25rem' }}>
              <label>Send From (Email Account) *</label>
              <select
                value={selectedEmailAccountId}
                onChange={(e) => setSelectedEmailAccountId(e.target.value)}
                disabled={submitting}
              >
                <option value="">-- Select Email Account --</option>
                {emailAccounts.map((a) => (
                  <option key={a.id} value={a.id}>{a.email || a.email_address || a.name}</option>
                ))}
              </select>
            </div>

            {/* Variable Inputs */}
            {detectedVariables.length > 0 && (
              <div style={{
                marginBottom: '1.5rem', padding: '1.25rem',
                background: 'linear-gradient(135deg, rgba(234, 179, 8, 0.05) 0%, rgba(245, 158, 11, 0.05) 100%)',
                border: '1px solid rgba(234, 179, 8, 0.2)',
                borderRadius: 'var(--radius-md)'
              }}>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: '0.5rem',
                  marginBottom: '1rem', fontSize: '0.85rem', fontWeight: '600',
                  color: '#d97706'
                }}>
                  <AlertCircle size={16} />
                  Fill in Template Variables
                </div>
                <p style={{
                  fontSize: '0.8rem', color: 'var(--color-text-secondary)',
                  marginBottom: '1rem', lineHeight: '1.5'
                }}>
                  Your email contains template variables. Please provide test values that will be used in the placement test email.
                </p>
                <div style={{ display: 'grid', gap: '0.75rem' }}>
                  {detectedVariables.map((varName) => (
                    <div key={varName} className="form-group" style={{ marginBottom: 0 }}>
                      <label style={{ fontSize: '0.8rem', marginBottom: '0.25rem' }}>
                        {formatVariableName(varName)} <span style={{ color: 'var(--color-text-muted)', fontWeight: '400' }}>{'{{' + varName + '}}'}</span>
                      </label>
                      <input
                        type="text"
                        value={variableValues[varName] || ''}
                        onChange={(e) => handleVariableChange(varName, e.target.value)}
                        placeholder={`Enter value for {{${varName}}}`}
                        disabled={submitting}
                        style={{ fontSize: '0.85rem' }}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Read-only Preview */}
            {preview && (
              <div style={{
                marginBottom: '1.5rem', padding: '1.25rem',
                background: 'var(--color-bg-primary)', border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius-md)'
              }}>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: '0.5rem',
                  marginBottom: '1rem', fontSize: '0.85rem', fontWeight: '600',
                  color: 'var(--color-text-secondary)'
                }}>
                  <Eye size={16} />
                  Email Preview {detectedVariables.length > 0 ? '(with variable replacements)' : '(Read-only)'}
                </div>
                <div style={{ marginBottom: '0.75rem' }}>
                  <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', marginBottom: '0.25rem' }}>Subject</div>
                  <div style={{
                    padding: '0.625rem 1rem', background: 'var(--color-bg-card)',
                    border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)',
                    fontSize: '0.9rem', color: 'var(--color-text-primary)'
                  }}>
                    {finalSubject || '(No subject)'}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', marginBottom: '0.25rem' }}>Body</div>
                  <div style={{
                    padding: '0.75rem 1rem', background: 'var(--color-bg-card)',
                    border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)',
                    fontSize: '0.875rem', lineHeight: '1.6', color: 'var(--color-text-primary)',
                    maxHeight: '300px', overflowY: 'auto'
                  }}
                    dangerouslySetInnerHTML={{ __html: finalBody || '<p style="color:var(--color-text-muted)">(No body content)</p>' }}
                  />
                </div>
              </div>
            )}

            <div style={{ display: 'flex', gap: '0.75rem', marginTop: '2rem' }}>
              <button
                type="button"
                onClick={() => navigate('/inbox-placement')}
                className="btn btn-ghost"
                style={{ flex: 1 }}
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                style={{ flex: 1 }}
                disabled={submitting || !selectedCampaignId || !selectedStepVariant || !selectedEmailAccountId || !allVariablesFilled}
              >
                {submitting ? (
                  <>
                    <RefreshCw size={16} className="animate-spin" />
                    <span>Sending...</span>
                  </>
                ) : (
                  <>
                    <Send size={16} />
                    <span>Run Placement Test</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
